import * as Sentry from "@sentry/vue";
/**
 * List of error messages that should be ignored by Sentry.
 * These errors are considered non-critical and will not be sent to Sentry.
 * @type {string[]}
 */
const ignoredMessages = [
  "Cannot read properties of null (reading 'token')",
  "Turnstile token expired",
  "Turnstile failed to load",
];

/**
 * Initialize Sentry error tracking for a Vue application.
 *
 * - Captures unhandled exceptions and performance data.
 * - Filters out specific non-critical errors defined in `ignoredMessages`.
 * - Only reports API failures whose HTTP status is 500.
 *
 * @function initSentry
 * @param {import("vue").VueConstructor} Vue - The Vue constructor instance.
 * @returns {void}
 *
 * @example
 * import Vue from "vue";
 * import initSentry from "./sentry";
 *
 * initSentry(Vue);
 */
function initSentry(Vue) {
  Sentry.init({
    Vue,
    dsn: process.env.VUE_APP_SENTRY_DSN,
    integrations: [Sentry.browserTracingIntegration()],
    tracesSampleRate: 1.0,

    beforeSend(event, hint) {
      const error = hint.originalException;

      /**
       * A rejected value does not always carry the axios `response` wrapper.
       * Promise rejections raised by lib/fetcher.js expose the status on the
       * error itself, so both shapes are checked before deciding that a
       * failure is worth reporting. Without the second lookup every expired
       * session reported one error per in-flight request.
       */
      const rawStatus =
        error?.response?.status ??
        error?.status ??
        error?.response?.data?.status;

      /**
       * Only a real HTTP status may silence an event. Without this guard a
       * `status` holding something else, for example the string "pending",
       * would become NaN, compare unequal to 500, and drop an error that
       * nothing else would have reported.
       */
      const httpStatus = Number(rawStatus);
      const isHttpStatus =
        Number.isFinite(httpStatus) && httpStatus >= 100 && httpStatus <= 599;

      if (isHttpStatus && httpStatus !== 500) {
        return null;
      }

      if (error instanceof Error && typeof error.message === 'string') {
        if (ignoredMessages.some(msg => error.message.includes(msg))) {
          return null;
        }
      }

      return event;
    },
  });
}

export default initSentry;
